// services/odata.service.ts
import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { forkJoin, Observable } from 'rxjs';
import { ODataFilterBuilder } from '@app/core/services/odata/builders/odata-filter-builter.class';
import { ODataQueryBuilder } from '@app/core/services/odata/builders/odata-query-builder.class';
import { ODataArea } from '@app/core/services/odata/types/odata-area.type';
import { ODataAreaClient } from '@app/core/services/odata/clients/odata-area.client';

/**
 * Main OData service for interacting with OData endpoints
 *
 * @example
 * // Inject the service
 * private odataService = inject(ODataService);
 *
 * // Access NTS area
 * this.odataService.nts.getCollection<MeteringPoint>('MeteringPoints')
 *
 * // Access DMC area
 * this.odataService.dmc.getCollection<ConnectionPoint>('ConnectionPoints')
 */
@Injectable({
  providedIn: 'root'
})
export class ODataService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = 'api';

  /**
   * Access NTS area
   *
   * @returns ODataAreaClient for NTS
   *
   * @example
   * this.odataService.nts.getCollection<MeteringPoint>('MeteringPoints', {
   *   $filter: "status eq 'Active'",
   *   $top: 50
   * }).subscribe(response => console.log(response.value));
   */
  get nts(): ODataAreaClient {
    return new ODataAreaClient(this.http, this.baseUrl, 'nts');
  }

  /**
   * Access DMC area
   *
   * @returns ODataAreaClient for DMC
   *
   * @example
   * this.odataService.dmc.getCollection<ConnectionPoint>('ConnectionPoints', {
   *   $filter: "type eq 'Consumption'",
   *   $top: 100
   * }).subscribe(response => console.log(response.value));
   */
  get dmc(): ODataAreaClient {
    return new ODataAreaClient(this.http, this.baseUrl, 'dmc');
  }

  /**
   * Access area by string (for dynamic usage)
   *
   * @param area - The area name ('nts' or 'dmc')
   * @returns ODataAreaClient for the specified area
   *
   * @example
   * const areaName: ODataArea = 'dmc';
   * this.odataService.area(areaName).getCollection<MeteringPoint>('MeteringPoints')
   *   .subscribe(response => console.log(response));
   */
  public area(area: ODataArea): ODataAreaClient {
    return new ODataAreaClient(this.http, this.baseUrl, area);
  }

  /**
   * Create a new filter builder (standalone, no area needed)
   *
   * @returns A new ODataFilterBuilder instance
   *
   * @example
   * const filter = this.odataService.filter()
   *   .equals('status', 'Active')
   *   .greaterThan('createdDate', new Date('2024-01-01'))
   *   .contains('name', 'meter');
   *
   * // Use with any area
   * this.odataService.dmc.getCollection<MeteringPoint>('MeteringPoints', filter)
   *   .subscribe(response => console.log(response));
   */
  public filter(): ODataFilterBuilder {
    return new ODataFilterBuilder();
  }

  /**
   * Create a new query builder (standalone, no area needed)
   *
   * @returns A new ODataQueryBuilder instance
   *
   * @example
   * const query = this.odataService.query()
   *   .filter(this.odataService.filter().equals('status', 'Active'))
   *   .orderBy('createdDate desc')
   *   .paginate(0, 50)
   *   .count();
   *
   * // Use with any area
   * this.odataService.dmc.getCollection<MeteringPoint>('MeteringPoints', query)
   *   .subscribe(response => {
   *     console.log('Data:', response.value);
   *     console.log('Total count:', response['@odata.count']);
   *   });
   */
  public query(): ODataQueryBuilder {
    return new ODataQueryBuilder();
  }

  /**
   * Batch multiple requests and execute them in parallel
   *
   * @param requests - Array of observables to execute
   * @returns Observable that emits array of results
   *
   * @example
   * const requests = [
   *   this.odataService.dmc.getCollection<MeteringPoint>('MeteringPoints'),
   *   this.odataService.dmc.getCollection<ConnectionPoint>('ConnectionPoints'),
   *   this.odataService.nts.getCollection<Transaction>('Transactions')
   * ];
   *
   * this.odataService.batch(requests).subscribe(([mp, cp, tx]) => {
   *   console.log('Metering Points:', mp);
   *   console.log('Connection Points:', cp);
   *   console.log('Transactions:', tx);
   * });
   */
  public batch<T extends any[]>(requests: { [K in keyof T]: Observable<T[K]> }): Observable<T> {
    return forkJoin(requests) as Observable<T>;
  }

  /**
   * Clear all caches across all areas
   *
   * @example
   * this.odataService.clearAllCaches();
   */
  public clearAllCaches(): void {
    this.nts.clearCache();
    this.dmc.clearCache();
  }
}
